import React from "react";
import { useSiteContent } from "@/lib/siteContent";

/**
 * LuxuryMarquee
 * Two continuous scrolling rows moving in opposite directions.
 * Row 1 (right -> left): Ingredients (bergamot, oud, iris, etc.)
 * Row 2 (left -> right): Offers & brand promises
 *
 * Content sources from Shopify Metaobjects (`marquee_content`) via useSiteContent.
 */

const INGREDIENTS = [
  "Bergamot",
  "Ambergris",
  "Bulgarian Rose",
  "Oud",
  "Iris Pallida",
  "Vetiver",
  "Sandalwood",
  "Cardamom",
  "Ylang Ylang",
  "Tonka Bean",
  "Cedarwood",
  "Saffron",
  "Frankincense",
  "Jasmine Sambac",
  "Patchouli",
  "Mandarin",
];

const OFFERS = [
  "Free Shipping on Orders Over $120",
  "Personalised Engraving Available · Add-On",
  "Pure Oils · 10ml Roll-Ons Available",
  "Hand-Blended in Small Batches",
  "25–35% Oil Concentration",
  "Long-Lasting Wear",
  "Cruelty-Free & Vegan",
  "Free 2ml Sample with Every Order",
  "French-Inspired Fragrance Notes · Hand-Poured with Love in Canada",
];

const Row = ({ items, direction = "left", speed = 45, dotClass, textClass }) => {
  // duplicate for seamless loop
  const doubled = [...items, ...items];
  const animClass = direction === "left" ? "sd-marquee-left" : "sd-marquee-right";
  return (
    <div className="sd-marquee-viewport" data-testid={`marquee-row-${direction}`}>
      <div className={`sd-marquee-track ${animClass}`} style={{ animationDuration: `${speed}s` }}>
        {doubled.map((item, i) => (
          <span key={`${item}-${i}`} className="sd-marquee-item">
            <span className={textClass}>{item}</span>
            <span className={dotClass} aria-hidden>·</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export const LuxuryMarquee = () => {
  const { marquee } = useSiteContent();
  const ingredients = (marquee && marquee.ingredients && marquee.ingredients.length > 0)
    ? marquee.ingredients
    : INGREDIENTS;
  const offers = (marquee && marquee.offers && marquee.offers.length > 0)
    ? marquee.offers
    : OFFERS;

  return (
    <section
      aria-label="Fragrance ingredients and offers"
      data-testid="luxury-marquee"
      className="relative overflow-hidden border-y border-[#D4B278]/15 bg-[#0B0A08]"
    >
      {/* Soft edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 sm:w-40 z-10 bg-gradient-to-r from-[#0B0A08] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 sm:w-40 z-10 bg-gradient-to-l from-[#0B0A08] to-transparent" />

      {/* Row 1: Ingredients (large serif italic, right -> left) */}
      <div className="py-6 sm:py-8">
        <Row
          items={ingredients}
          direction="left"
          speed={55}
          textClass="font-serif italic text-[#FAF8F5] text-[28px] sm:text-[38px] lg:text-[46px] leading-none tracking-[-0.005em]"
          dotClass="text-[#C5A059] text-[28px] sm:text-[38px] lg:text-[46px] mx-6 sm:mx-9 lg:mx-12"
        />
      </div>

      {/* Divider hairline */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-[#D4B278]/25 to-transparent" />

      {/* Row 2: Offers (small uppercase sans, left -> right) */}
      <div className="py-3.5 sm:py-4">
        <Row
          items={offers}
          direction="right"
          speed={65}
          textClass="uppercase tracking-[0.22em] text-[#FAF8F5]/75 text-[10px] sm:text-[11px] lg:text-[12px]"
          dotClass="text-[#C5A059]/70 text-xs mx-5 sm:mx-7"
        />
      </div>
    </section>
  );
};

export default LuxuryMarquee;
